import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - 淫母日记"
          description="您访问的文章不存在或已被删除。请浏览淫母日记的其他精彩内容。"
          canonicalUrl="https://elinggong.cn"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-file-unknow-line text-3xl text-foreground-500"></i>
              </div>
              <h1 className="text-2xl font-heading font-semibold text-foreground-100 mb-3">
                文章未找到
              </h1>
              <p className="text-sm text-foreground-500 mb-8">
                您访问的文章可能已被删除或不存在
              </p>
              <Link
                to="/"
                className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-label text-sm hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleTitle = `${article.title} - 淫母日记`;
  const articleUrl = `https://elinggong.cn/article/${article.id}`;

  return (
    <>
      <SEOHead
        title={articleTitle}
        description={article.excerpt}
        keywords={`淫母日记,${article.category},${article.tags.join(',')},成人日记,日记文学`}
        canonicalUrl={articleUrl}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.date}
        articleTags={article.tags}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          datePublished: article.date,
          dateModified: article.date,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '淫母日记',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': articleUrl,
          },
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: articleTitle,
          url: articleUrl,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <article className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: article.category, path: `/category/${article.categorySlug}` },
                  { name: article.title },
                ]}
              />
            </div>

            <div className="mb-2">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 rounded-full text-xs font-label bg-primary-500/15 text-primary-400 hover:bg-primary-500/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                {article.category}
              </Link>
            </div>

            <h1 className="mt-4 text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 leading-tight">
              {article.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.readCount > 10000
                  ? `${(article.readCount / 10000).toFixed(1)}万 阅读`
                  : `${article.readCount} 阅读`}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 rounded text-xs font-label bg-background-100 text-foreground-500"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 aspect-[16/9] overflow-hidden rounded-lg">
              <div className="w-full h-full">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  title={`${article.title} - 淫母日记`}
                />
              </div>
            </div>

            <div
              className="mt-10 prose prose-invert max-w-none
                prose-p:text-foreground-300 prose-p:leading-loose prose-p:text-base prose-p:mb-5 prose-p:indent-8
                prose-strong:text-foreground-100
                prose-h2:text-foreground-100 prose-h2:text-xl prose-h2:font-heading prose-h2:font-semibold
                prose-h3:text-foreground-200 prose-h3:text-lg prose-h3:font-heading
                prose-blockquote:border-primary-500 prose-blockquote:text-foreground-400 prose-blockquote:bg-background-100 prose-blockquote:py-3 prose-blockquote:px-5 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
                prose-li:text-foreground-300"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs font-label bg-background-100 text-foreground-400"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-200 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享">
                    <i className="ri-share-forward-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-200 transition-colors cursor-pointer whitespace-nowrap" aria-label="收藏">
                    <i className="ri-bookmark-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-3xl mx-auto px-4 md:px-6 mt-14">
              <h2 className="text-xl font-heading font-semibold text-foreground-100 mb-6">
                相关推荐
              </h2>
              <div className="flex flex-col gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} variant="compact" />
                ))}
              </div>
            </section>
          )}

          <div className="max-w-3xl mx-auto px-4 md:px-6 mt-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-200 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}